// Improved Cart Quantity Selectors JavaScript (fixed + idempotent)
(() => {
  // Show quantity error message
  function showQuantityError(container, message) {
    if (!container) return;

    container.classList.add('error');

    const existingError = container.querySelector('.quantity-error-message');
    if (existingError) existingError.remove();

    const errorDiv = document.createElement('div');
    errorDiv.className = 'quantity-error-message';
    errorDiv.textContent = message;
    errorDiv.style.cssText = `
      position: absolute;
      top: -30px;
      left: 50%;
      transform: translateX(-50%);
      background: #dc2626;
      color: white;
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 500;
      white-space: nowrap;
      z-index: 1000;
      animation: fadeInDown 0.3s ease;
    `;

    container.style.position = 'relative';
    container.appendChild(errorDiv);

    setTimeout(() => {
      container.classList.remove('error');
      if (errorDiv && errorDiv.parentNode) errorDiv.remove();
    }, 3000);

    if ('vibrate' in navigator) navigator.vibrate([100, 50, 100]);
  }

  // Inventory / max enforcement
  function checkInventoryLimits(input, newValue) {
    if (!input) return { valid: true };

    const invAttr = input.getAttribute('data-inventory-quantity');
    const inventoryQuantity = invAttr === null ? null : parseInt(invAttr, 10);
    const inventoryPolicy = input.getAttribute('data-inventory-policy');
    const maxAllowed = parseInt(input.getAttribute('max') || '', 10);

    // If inventory is managed and policy is not 'continue', enforce limits
    if (inventoryQuantity !== null && !Number.isNaN(inventoryQuantity) && inventoryPolicy !== 'continue') {
      if (newValue > inventoryQuantity) {
        return { valid: false, message: `Only ${inventoryQuantity} available in stock` };
      }
    }

    // Check against max attribute (if present)
    if (!Number.isNaN(maxAllowed) && maxAllowed > 0 && newValue > maxAllowed) {
      return { valid: false, message: `Maximum quantity is ${maxAllowed}` };
    }

    return { valid: true };
  }

  // Attach listeners to a single quantity input ONCE
  function bindQuantityInput(input) {
    if (!input) return;
    if (input.dataset.qtyBound === '1') return; // ✅ prevents duplicate listeners
    input.dataset.qtyBound = '1';

    const quantityContainer = input.closest('.quantity');
    if (!quantityContainer) return;

    const minusButton = quantityContainer.querySelector('button[name="minus"]');
    const plusButton  = quantityContainer.querySelector('button[name="plus"]');

    function getMin() {
      const min = parseInt(input.getAttribute('min') || '1', 10);
      return Number.isNaN(min) ? 1 : min;
    }

    function getMax() {
      const max = parseInt(input.getAttribute('max') || '999', 10);
      return Number.isNaN(max) ? 999 : max;
    }

    function getValue() {
      const v = parseInt(input.value || '0', 10);
      return Number.isNaN(v) ? 0 : v;
    }

    function updateButtonStates() {
      const value = getValue();
      const min = getMin();
      const max = getMax();

      if (minusButton) minusButton.disabled = value <= min;
      if (plusButton)  plusButton.disabled  = value >= max;
    }

    // Initial state
    updateButtonStates();

    // Input typing feedback
    input.addEventListener('input', () => {
      updateButtonStates();

      quantityContainer.classList.remove('error', 'success');
      quantityContainer.classList.add('loading');

      setTimeout(() => {
        quantityContainer.classList.remove('loading');
        quantityContainer.classList.add('success');

        setTimeout(() => quantityContainer.classList.remove('success'), 1500);
      }, 300);
    });

    // Keyboard restrictions (numbers + navigation)
    input.addEventListener('keydown', (e) => {
      const allowed = [8, 9, 27, 13, 46]; // backspace, tab, esc, enter, delete
      const nav = (e.keyCode >= 35 && e.keyCode <= 39); // home/end/left/right

      if (
        allowed.includes(e.keyCode) ||
        nav ||
        (e.keyCode === 65 && e.ctrlKey) || // Ctrl+A
        (e.keyCode === 67 && e.ctrlKey) || // Ctrl+C
        (e.keyCode === 86 && e.ctrlKey) || // Ctrl+V
        (e.keyCode === 88 && e.ctrlKey)    // Ctrl+X
      ) return;

      // Only digits
      const isTopRowDigit = e.keyCode >= 48 && e.keyCode <= 57;
      const isNumpadDigit = e.keyCode >= 96 && e.keyCode <= 105;
      if (e.shiftKey || (!isTopRowDigit && !isNumpadDigit)) e.preventDefault();
    });

    // Validate on blur
    input.addEventListener('blur', () => {
      const min = getMin();
      let value = getValue();

      if (value < min) {
        value = min;
        input.value = String(min);
        showQuantityError(quantityContainer, `Minimum quantity is ${min}`);
      } else {
        const validation = checkInventoryLimits(input, value);
        if (!validation.valid) {
          // Fall back to max (or current)
          const max = getMax();
          input.value = String(max);
          showQuantityError(quantityContainer, validation.message);
        }
      }

      updateButtonStates();
    });

    // Select all on focus
    input.addEventListener('focus', () => setTimeout(() => input.select(), 0));
  }

  // Attach button listeners ONCE
  function bindQuantityButton(button) {
    if (!button) return;
    if (button.dataset.qtyBtnBound === '1') return; // ✅ prevents duplicate listeners
    button.dataset.qtyBtnBound = '1';

    button.addEventListener('click', (e) => {
      if (button.disabled) return;

      const quantityContainer = button.closest('.quantity');
      const input = quantityContainer?.querySelector('.quantity__input');
      if (!quantityContainer || !input) return;

      const isPlus = button.getAttribute('name') === 'plus';

      if (isPlus) {
        const currentValue = parseInt(input.value || '0', 10) || 0;
        const newValue = currentValue + 1;

        const validation = checkInventoryLimits(input, newValue);
        if (!validation.valid) {
          e.preventDefault();
          e.stopPropagation();
          showQuantityError(quantityContainer, validation.message);
          return;
        }
      }

      // Visual feedback
      button.style.transform = 'scale(0.95)';
      setTimeout(() => (button.style.transform = ''), 150);

      if ('vibrate' in navigator) navigator.vibrate(50);

      quantityContainer.classList.add('loading');
      setTimeout(() => {
        quantityContainer.classList.remove('loading');
        quantityContainer.classList.add('success');
        setTimeout(() => quantityContainer.classList.remove('success'), 1000);
      }, 500);
    });

    // Hover feedback
    button.addEventListener('mouseenter', () => {
      if (!button.disabled) button.style.transform = 'scale(1.05)';
    });

    button.addEventListener('mouseleave', () => {
      button.style.transform = '';
    });
  }

  // Init quantity inputs within cart drawer + cart page
  function initQuantityInputs() {
    const cartDrawer = document.querySelector('#CartDrawer');
    const cartItems = document.querySelector('.cart-items');
    const containers = [cartDrawer, cartItems].filter(Boolean);
    if (!containers.length) return;

    containers.forEach((container) => {
      container.querySelectorAll('.quantity__input').forEach(bindQuantityInput);
      container.querySelectorAll('.quantity__button').forEach(bindQuantityButton);
    });
  }

  // Smooth transitions when cart items are removed
  function bindRemoveTransitionsOnce() {
    if (document.documentElement.dataset.cartRemoveFxBound === '1') return;
    document.documentElement.dataset.cartRemoveFxBound = '1';

    document.addEventListener('click', (e) => {
      const btn = e.target?.closest?.('cart-remove-button');
      if (!btn) return;

      const cartItem = btn.closest('.cart-item');
      if (!cartItem) return;

      cartItem.style.transition = 'all 0.3s ease';
      cartItem.style.opacity = '0.5';
      cartItem.style.transform = 'translateX(-20px)';
    });
  }

  // Keyboard shortcuts for cart drawer
  function bindCartKeyboardOnce() {
    if (document.documentElement.dataset.cartKbBound === '1') return;
    document.documentElement.dataset.cartKbBound = '1';

    document.addEventListener('keydown', (e) => {
      const drawerEl = document.querySelector('#CartDrawer');
      // Some themes use aria-hidden/class instead of .open; we only gate lightly
      if (!drawerEl) return;

      // ESC to close cart (if close button exists)
      if (e.key === 'Escape') {
        const closeButton = drawerEl.querySelector('.drawer__close');
        if (closeButton) closeButton.click();
      }

      // Arrow keys for quantity adjustment when focused on input
      const active = document.activeElement;
      if (!active || !active.classList?.contains('quantity__input')) return;

      const quantityContainer = active.closest('.quantity');
      if (!quantityContainer) return;

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const plusButton = quantityContainer.querySelector('button[name="plus"]');
        if (plusButton && !plusButton.disabled) plusButton.click();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        const minusButton = quantityContainer.querySelector('button[name="minus"]');
        if (minusButton && !minusButton.disabled) minusButton.click();
      }
    });
  }

  // Observe cart drawer open/class changes to re-init
  function observeCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || cartDrawer.dataset.qtyObserverBound === '1') return;
    cartDrawer.dataset.qtyObserverBound = '1';

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && (m.attributeName === 'open' || m.attributeName === 'class')) {
          setTimeout(initQuantityInputs, 50);
          break;
        }
      }
    });

    observer.observe(cartDrawer, { attributes: true, attributeFilter: ['open', 'class'] });
  }

  // Public hook (optional)
  window.initImprovedQuantitySelectors = function () {
    setTimeout(initQuantityInputs, 50);
  };

  // ✅ Single DOMContentLoaded
  document.addEventListener('DOMContentLoaded', () => {
    initQuantityInputs();
    bindRemoveTransitionsOnce();
    bindCartKeyboardOnce();
    observeCartDrawer();

    document.addEventListener('cart:updated', () => {
      setTimeout(initQuantityInputs, 50);
    });
  });
})();
